import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Search, Eye, Users, Database } from "lucide-react";
import { toast } from "sonner";

export default function AdminBeneficiaryLibrary() {
  const [items, setItems] = useState([]);
  const [masterEntries, setMasterEntries] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [tab, setTab] = useState("master"); // "master" | "all"

  const load = async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers/beneficiary-library?search=${encodeURIComponent(q)}&include_deleted=true&limit=200`);
      setItems(data.items || []);
      setMasterEntries(data.master_entries || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doSearch = () => load(search);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold" data-testid="bene-library-title">Beneficiary Master Library</h1>
            <p className="text-sm text-muted-foreground mt-1">All registered beneficiaries with ManaRecharge provider data. Deduped by Account + IFSC.</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Database className="h-4 w-4" />
            <span>{masterEntries.length} unique accounts · {items.length} total records</span>
          </div>
        </div>

        <div className="flex gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, account number, or IFSC..."
            className="max-w-sm"
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            data-testid="bene-search"
          />
          <Button variant="outline" onClick={doSearch} data-testid="bene-search-btn">
            <Search className="h-4 w-4 mr-1" /> Search
          </Button>
        </div>

        {/* Tab toggle */}
        <div className="flex gap-1 border-b">
          <button onClick={() => setTab("master")} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "master" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            Master Library ({masterEntries.length})
          </button>
          <button onClick={() => setTab("all")} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "all" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            All Records ({items.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : tab === "master" ? (
          /* Master view: unique account+ifsc combos */
          <div className="space-y-2">
            {masterEntries.map((m, i) => (
              <div key={i} className="surface-card p-4 flex items-start justify-between gap-4" data-testid={`master-entry-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{m.account_holder_name}</div>
                  <div className="text-xs text-muted-foreground mt-1 font-mono">
                    A/C: {m.account_number} · IFSC: {m.ifsc}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {m.provider_bene_id ? (
                      <Badge variant="outline" className="text-[10px] border-green-300 text-green-700">Registered: {m.provider_bene_id}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-red-300 text-red-600">Not registered</Badge>
                    )}
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Users className="h-3 w-3" /> Used by {m.used_by?.length || 0} partner(s)
                    </span>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setDetailItem(m); setDetailOpen(true); }} data-testid={`master-detail-${i}`}>
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {masterEntries.length === 0 && <div className="text-center py-8 text-muted-foreground">No beneficiaries found</div>}
          </div>
        ) : (
          /* All records view */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-xs text-muted-foreground">
                <th className="text-left px-2 py-2">Name</th>
                <th className="text-left px-2 py-2">Account</th>
                <th className="text-left px-2 py-2">IFSC</th>
                <th className="text-left px-2 py-2">Provider ID</th>
                <th className="text-left px-2 py-2">Partner</th>
                <th className="text-left px-2 py-2">Status</th>
                <th className="text-left px-2 py-2">Created</th>
                <th className="px-2 py-2"></th>
              </tr></thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.id} className={`border-b hover:bg-muted/30 ${b.deleted_at ? "opacity-50" : ""}`}>
                    <td className="px-2 py-2">{b.account_holder_name}</td>
                    <td className="px-2 py-2 font-mono text-xs">{b.account_number}</td>
                    <td className="px-2 py-2 font-mono text-xs">{b.ifsc}</td>
                    <td className="px-2 py-2 font-mono text-xs">{b.provider_bene_id || "—"}</td>
                    <td className="px-2 py-2 text-xs">{b.user_id?.slice(0, 8)}...</td>
                    <td className="px-2 py-2">
                      {b.deleted_at ? (
                        <Badge variant="outline" className="text-[10px] border-red-300 text-red-600">Deleted</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] border-green-300 text-green-700">Active</Badge>
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString("en-IN")}</td>
                    <td className="px-2 py-2">
                      <Button variant="ghost" size="sm" onClick={() => { setDetailItem(b); setDetailOpen(true); }}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 && <div className="text-center py-8 text-muted-foreground">No records found</div>}
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Beneficiary Details</DialogTitle>
          </DialogHeader>
          {detailItem && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground text-xs">Account Holder</span><div className="font-medium">{detailItem.account_holder_name}</div></div>
                <div><span className="text-muted-foreground text-xs">Account Number</span><div className="font-mono">{detailItem.account_number}</div></div>
                <div><span className="text-muted-foreground text-xs">IFSC</span><div className="font-mono">{detailItem.ifsc}</div></div>
                <div><span className="text-muted-foreground text-xs">Provider</span><div>{detailItem.provider || "—"}</div></div>
                <div><span className="text-muted-foreground text-xs">Provider Bene ID</span><div className="font-mono">{detailItem.provider_bene_id || "—"}</div></div>
                {detailItem.first_added && <div><span className="text-muted-foreground text-xs">First Added</span><div>{new Date(detailItem.first_added).toLocaleString("en-IN")}</div></div>}
              </div>
              {detailItem.provider_raw_response && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1 mt-3">ManaRecharge Raw Response</div>
                  <pre className="bg-muted rounded-md p-3 text-[11px] overflow-x-auto max-h-60 whitespace-pre-wrap">
                    {JSON.stringify(detailItem.provider_raw_response, null, 2)}
                  </pre>
                </div>
              )}
              {detailItem.used_by && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1 mt-3">Used By ({detailItem.used_by.length})</div>
                  <div className="space-y-1">
                    {detailItem.used_by.map((u, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span>{u.nickname || u.id?.slice(0, 8)} <span className="text-muted-foreground">({u.user_id?.slice(0, 8)}...)</span></span>
                        <span>{u.deleted ? <Badge variant="outline" className="text-[9px]">deleted</Badge> : <Badge variant="outline" className="text-[9px] border-green-300 text-green-700">active</Badge>}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
